/**
 * ModifierPanel Component
 *
 * Two-group modifier configuration: Safety modifiers (Shield icon, red accent)
 * and Style modifiers (Palette icon, blue accent). Includes a redundancy
 * warning when SCARF engine and SCARF Threat Check modifier are both active.
 */

import { useCallback, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Shield, Palette, AlertTriangle, Check } from 'lucide-react'
import type { ModifierType, EngineType } from '@personic/shared/types/persona-engine'
import { MODIFIER_LABELS } from '@personic/shared/types/persona-engine'

// ─── Modifier metadata ──────────────────────────────────────────────────────

interface ModifierMeta {
  key: ModifierType
  label: string
  description: string
  tier: 'safety' | 'style'
  warningText?: string
}

const MODIFIER_META: ModifierMeta[] = [
  {
    key: 'scarf_threat_check',
    label: MODIFIER_LABELS.scarf_threat_check,
    description:
      'Applies SCARF domain scoring as a safety overlay regardless of primary engine. Overrides final dimensions when threat is detected.',
    tier: 'safety',
    warningText:
      'This modifier overrides engine output when threat domains are detected. Use with caution in non-safety-critical contexts.',
  },
  {
    key: 'politeness_theory',
    label: MODIFIER_LABELS.politeness_theory,
    description:
      'Adjusts persona output for face-saving strategies (positive and negative politeness). Recommended for customer-facing contexts.',
    tier: 'style',
  },
  {
    key: 'cat_damping',
    label: MODIFIER_LABELS.cat_damping,
    description:
      'Attenuates communication accommodation convergence to prevent over-mirroring. Smooths adaptation signals over time.',
    tier: 'style',
  },
]

// ─── Types ───────────────────────────────────────────────────────────────────

interface ModifierPanelProps {
  activeModifiers: ModifierType[]
  onChange: (modifiers: ModifierType[]) => void
  primaryEngine?: EngineType
  disabled?: boolean
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function ModifierPanel({
  activeModifiers,
  onChange,
  primaryEngine,
  disabled = false,
}: ModifierPanelProps) {
  const safetyModifiers = useMemo(
    () => MODIFIER_META.filter((m) => m.tier === 'safety'),
    []
  )
  const styleModifiers = useMemo(
    () => MODIFIER_META.filter((m) => m.tier === 'style'),
    []
  )

  const showRedundancyWarning =
    primaryEngine === 'scarf' && activeModifiers.includes('scarf_threat_check')

  const toggleModifier = useCallback(
    (key: ModifierType) => {
      if (disabled) return
      const next = activeModifiers.includes(key)
        ? activeModifiers.filter((m) => m !== key)
        : [...activeModifiers, key]
      onChange(next)
    },
    [activeModifiers, onChange, disabled]
  )

  return (
    <div className="space-y-6">
      {/* Redundancy warning */}
      <AnimatePresence>
        {showRedundancyWarning && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden"
          >
            <div className="flex items-start gap-3 p-4 rounded-xl border-2 border-amber-300 bg-amber-50">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-amber-800">
                  Redundancy Detected
                </p>
                <p className="text-sm text-amber-700 mt-1">
                  SCARF Threat Check modifier is active alongside the SCARF primary engine.
                  The threat-check overlay may produce redundant scoring since the SCARF engine
                  already evaluates all five domains. Consider disabling the modifier unless
                  you need the safety override behavior.
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Safety modifiers */}
      <ModifierGroup
        icon={Shield}
        title="Safety Modifiers"
        accentColor="red"
        modifiers={safetyModifiers}
        activeModifiers={activeModifiers}
        onToggle={toggleModifier}
        disabled={disabled}
      />

      {/* Style modifiers */}
      <ModifierGroup
        icon={Palette}
        title="Style Modifiers"
        accentColor="blue"
        modifiers={styleModifiers}
        activeModifiers={activeModifiers}
        onToggle={toggleModifier}
        disabled={disabled}
      />
    </div>
  )
}

// ─── Sub-components ──────────────────────────────────────────────────────────

interface ModifierGroupProps {
  icon: React.ElementType
  title: string
  accentColor: 'red' | 'blue'
  modifiers: ModifierMeta[]
  activeModifiers: ModifierType[]
  onToggle: (key: ModifierType) => void
  disabled: boolean
}

function ModifierGroup({
  icon: Icon,
  title,
  accentColor,
  modifiers,
  activeModifiers,
  onToggle,
  disabled,
}: ModifierGroupProps) {
  const accent = accentColor === 'red'
    ? {
        headerBg: 'bg-red-50',
        headerText: 'text-red-700',
        iconColor: 'text-red-500',
        activeBorder: 'border-red-400',
        activeBg: 'bg-red-50',
        checkBg: 'bg-red-500',
      }
    : {
        headerBg: 'bg-blue-50',
        headerText: 'text-blue-700',
        iconColor: 'text-blue-500',
        activeBorder: 'border-blue-400',
        activeBg: 'bg-blue-50',
        checkBg: 'bg-blue-500',
      }

  return (
    <div>
      {/* Group header */}
      <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${accent.headerBg} mb-3`}>
        <Icon className={`w-4 h-4 ${accent.iconColor}`} />
        <h4 className={`text-xs font-bold uppercase tracking-wider ${accent.headerText}`}>
          {title}
        </h4>
      </div>

      {/* Modifier cards */}
      <div className="space-y-2">
        {modifiers.map((mod, index) => {
          const isActive = activeModifiers.includes(mod.key)

          return (
            <motion.div
              key={mod.key}
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.05, duration: 0.2 }}
            >
              <button
                type="button"
                onClick={() => onToggle(mod.key)}
                disabled={disabled}
                className={`
                  w-full text-left p-4 rounded-xl border-2 transition-all duration-150
                  ${isActive
                    ? `${accent.activeBorder} ${accent.activeBg}`
                    : 'border-gray-200 bg-white hover:border-gray-300'
                  }
                  ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                `}
              >
                <div className="flex items-start gap-3">
                  {/* Checkbox indicator */}
                  <div
                    className={`
                      mt-0.5 flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center
                      transition-colors duration-150
                      ${isActive
                        ? `${accent.checkBg} border-transparent`
                        : 'border-gray-300 bg-white'
                      }
                    `}
                  >
                    {isActive && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <span className="font-semibold text-sm text-gray-900">{mod.label}</span>
                    <p className="mt-1 text-sm text-gray-500 leading-relaxed">
                      {mod.description}
                    </p>

                    {/* Override warning for safety modifiers */}
                    {mod.warningText && isActive && (
                      <motion.p
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        className="mt-2 text-xs text-amber-600 bg-amber-50 rounded-lg px-3 py-2 border border-amber-200"
                      >
                        {mod.warningText}
                      </motion.p>
                    )}
                  </div>
                </div>
              </button>
            </motion.div>
          )
        })}
      </div>
    </div>
  )
}
